import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { IconHire } from '@components/icons/hire';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { api } from 'api';

import { Loading } from 'components/Loading';
import { useAuthenticatedUser } from 'features/authentication';
import { Modal, ModalProps, useModal } from 'features/modals';
import { useStageEntryJourneyPrompt } from 'integrations/journeys/job-triggers/use-stage-entry-journey';
import { useStore } from 'models/Provider';

type Props = ModalProps<{
  applicationId: number;
  applicationName: string;
  jobId: number;
  openOnboardModal?: boolean;
}>;

export const HireApplicantModal = (props: Props) => {
  const { applicationId, applicationName, jobId, closeModal, openOnboardModal } = props;
  const { showError, showSuccess } = useToaster();
  const { showModal, setModalAfterClose } = useModal();
  const me = useAuthenticatedUser();
  const { applicationStore, t } = useStore();
  const queryClient = useQueryClient();
  const { promptOutcomeJourney } = useStageEntryJourneyPrompt(jobId);

  const submit = useMutation(() => api.applications.hire(applicationId), {
    onSuccess: () => {
      applicationStore.load(applicationId);
      setTimeout(() => queryClient.invalidateQueries(['applicants']), 2000);
      applicationStore.hire(applicationId, me);
      showSuccess(t('application.hire.success', applicationName));
      if (openOnboardModal) {
        // Chained, not stacked: the onboarding flow keeps priority, and the journey
        // prompt opens once it closes by any route (its own buttons, the backdrop,
        // Escape, the browser back button). Raising it here rather than at a sixth
        // call site covers both callers of this modal — use-application-options.tsx
        // and Application.changeStage — and with them the drag-to-Hired route, which
        // suppresses the *stage* prompt and delegates to HIRE_APPLICANT.
        setModalAfterClose('ONBOARD_APPLICANT', () =>
          promptOutcomeJourney([applicationId], 'hired')
        );
        showModal('ONBOARD_APPLICANT', {
          _container: { autoWidth: false },
          applicationId: applicationId,
          jobId: jobId,
        });
      } else {
        // Outcome wins: a stage prompt chained on this modal must stand down
        // when the hire succeeded — the hired-outcome prompt below is the
        // more specific event.
        setModalAfterClose('HIRE_APPLICANT', null);
        closeModal();
        promptOutcomeJourney([applicationId], 'hired');
      }
    },
    onError: () => {
      showError(t('application.hire.error', applicationName));
    },
  });

  return (
    <Modal>
      <Modal.Header text={t('hiremodal.title')} />
      <Stack space={4}>
        <Text text={t('hiremodal.confirmtext')} />
        <Text font="bold16" text={applicationName} />
        {submit.isLoading && <Loading />}
      </Stack>

      <Modal.Actions>
        <Button
          isDisabled={submit.isLoading}
          variant="secondary"
          onClick={closeModal}
          text={t('hiremodal.button.cancel')}
        />

        <Button
          isDisabled={submit.isLoading}
          iconLeft={<IconHire />}
          onClick={() => submit.mutate()}
          text={t('hiremodal.button.submit')}
        />
      </Modal.Actions>
    </Modal>
  );
};
